// File: app/src/components/workout/FinishBySheets.js
//
// Finish-by deadline (#8). FinishBySheet sets the time on the start screen;
// TrimSuggestionSheet puts the coach's one trim in front of the user at a
// break. Both are bottom sheets with the actions in thumb reach.

import React, { useEffect, useState } from "react";
import DetailSheet from "../session/generate/DetailSheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SwitchRow } from "../profile/ProfileParts";
import { formatClock, resolveFinishBy } from "../../utils/finishBy";

const toInputValue = (ms) => {
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const minutesText = (sec) => {
  const min = Math.max(1, Math.round(sec / 60));
  return `${min} min`;
};

/**
 * Set or clear today's finish-by time. Saves when the sheet closes.
 *
 * `plannedSec` is the session's length, to say up front whether starting now
 * already runs past the time picked.
 */
export const FinishBySheet = ({ open, onOpenChange, deadline, setDeadline, plannedSec }) => {
  const [time, setTime] = useState("");
  const [alarm, setAlarm] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTime(deadline ? toInputValue(deadline.at) : "");
    setAlarm(!!deadline?.alarm);
  }, [open, deadline]);

  const now = Date.now();
  const at = resolveFinishBy(time, now);
  const plannedEnd = plannedSec > 0 ? now + plannedSec * 1000 : null;
  const overSec = at != null && plannedEnd != null ? Math.round((plannedEnd - at) / 1000) : null;

  const close = (next) => {
    if (next) return;
    setDeadline(at, { alarm });
    onOpenChange(false);
  };

  return (
    <DetailSheet
      open={open}
      onOpenChange={close}
      title="Finish by"
      description="Khronos keeps an eye on the clock and suggests a trim at a rest if you're heading past this time."
      showDescription
      footer={
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-12 text-base"
            onClick={() => {
              setTime("");
              setDeadline(null);
              onOpenChange(false);
            }}
          >
            {deadline ? "Clear" : "Cancel"}
          </Button>
          <Button type="button" className="h-12 text-base font-semibold" onClick={() => close(false)}>
            Done
          </Button>
        </div>
      }
    >
      <div className="grid gap-2">
        <label htmlFor="finish-by-time" className="mb-0 text-sm font-semibold text-foreground">
          Time
        </label>
        <Input
          id="finish-by-time"
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="h-12 text-lg tabular-nums"
        />
        {overSec != null && (
          <p className={overSec > 0 ? "text-sm text-amber-400" : "text-sm text-muted-foreground"}>
            {overSec > 0
              ? `Starting now, this session ends about ${formatClock(plannedEnd)}: ${minutesText(overSec)} late. You'll get a trim suggestion at the first rest.`
              : `Starting now, this session ends about ${formatClock(plannedEnd)}.`}
          </p>
        )}
      </div>
      <SwitchRow
        id="finish-by-alarm"
        label="Alarm at this time"
        description="Plays the end-of-session sound when the time comes."
        checked={alarm}
        onCheckedChange={setAlarm}
      />
    </DetailSheet>
  );
};

/**
 * The coach's trim suggestion. Swiping it away is "Keep going".
 */
export const TrimSuggestionSheet = ({ offer, onTrim, onKeepGoing }) => {
  if (!offer) return null;
  return (
    <DetailSheet
      open
      onOpenChange={(next) => {
        if (!next) onKeepGoing();
      }}
      title={`Running past ${offer.deadlineLabel}`}
      description={`At this pace you'll finish about ${offer.projectedLabel}, ${minutesText(offer.overSec)} late.`}
      showDescription
      footer={
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" className="h-12 text-base" onClick={onKeepGoing}>
            Keep going
          </Button>
          <Button type="button" className="h-12 text-base font-semibold" onClick={onTrim}>
            Trim
          </Button>
        </div>
      }
    >
      <div className="grid gap-1.5">
        <p className="text-base font-semibold leading-snug">{offer.text}.</p>
        <p className="text-sm text-muted-foreground">
          {offer.landsOnTime
            ? `That finishes by ${offer.deadlineLabel}.`
            : `That's as much as can come out. You'll still be a little past ${offer.deadlineLabel}.`}
        </p>
      </div>
    </DetailSheet>
  );
};
